import React, { useEffect, useMemo, useState } from 'react';
import {
  ArchiveBoxIcon,
  ChevronDownIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import BaseInput from '../../components/ui/base-input';
import type { AtsCandidateListItem } from '../types';
import type { AtsFolderNode } from '../utils/folders';

const expandablePaths = (nodes: AtsFolderNode[]): string[] => (
  nodes.flatMap((node) => (node.children.length ? [node.path, ...expandablePaths(node.children)] : []))
);

const matchesQuery = (node: AtsFolderNode, query: string): boolean => {
  const normalized = query.trim().toLocaleLowerCase('ru-RU');
  return !normalized
    || node.name.toLocaleLowerCase('ru-RU').includes(normalized)
    || node.path.toLocaleLowerCase('ru-RU').includes(normalized)
    || node.children.some((child) => matchesQuery(child, query));
};

const candidateTitle = (item: AtsCandidateListItem): string => (
  item.full_name?.trim() || 'Без имени'
);

export const FolderDeleteModal = ({
  folderName,
  folderPath,
  candidates,
  folders,
  disabled,
  onClose,
  onMoveSelected,
  onDeleteWithResumes,
}: {
  folderName: string;
  folderPath: string;
  candidates: AtsCandidateListItem[];
  folders: AtsFolderNode[];
  disabled: boolean;
  onClose: () => void;
  onMoveSelected: (candidateIds: string[], targetFolderPath: string) => void;
  onDeleteWithResumes: () => void;
}) => {
  const [step, setStep] = useState<'resumes' | 'destination'>('resumes');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(candidates.map((item) => item.candidate_id)));
  const [selectedFolder, setSelectedFolder] = useState('');
  const [query, setQuery] = useState('');
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(() => new Set(expandablePaths(folders)));

  useEffect(() => {
    setSelectedIds(new Set(candidates.map((item) => item.candidate_id)));
    setStep('resumes');
    setSelectedFolder('');
    setQuery('');
    setExpandedPaths(new Set(expandablePaths(folders)));
  }, [candidates, folders, folderPath]);

  const allSelected = candidates.length > 0 && selectedIds.size === candidates.length;
  const someSelected = selectedIds.size > 0;

  const isForbiddenDestination = (path: string): boolean => (
    path === folderPath || path.startsWith(`${folderPath}/`)
  );

  const selectedNode = useMemo(() => {
    const find = (nodes: AtsFolderNode[]): AtsFolderNode | undefined => {
      for (const node of nodes) {
        if (node.path === selectedFolder) return node;
        const nested = find(node.children);
        if (nested) return nested;
      }
      return undefined;
    };
    return find(folders);
  }, [folders, selectedFolder]);

  const selectedIsAllowed = Boolean(
    selectedNode
    && selectedNode.children.length === 0
    && !isForbiddenDestination(selectedNode.path),
  );

  const toggleId = (candidateId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(candidateId)) next.delete(candidateId);
      else next.add(candidateId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(candidates.map((item) => item.candidate_id)));
  };

  const renderNode = (node: AtsFolderNode, level = 0): React.ReactNode => {
    if (!matchesQuery(node, query)) return null;
    const hasChildren = node.children.length > 0;
    const expanded = Boolean(query.trim()) || expandedPaths.has(node.path);
    const forbidden = isForbiddenDestination(node.path);
    const selectable = !hasChildren && !forbidden;
    const selected = selectedFolder === node.path;
    return (
      <div key={node.path}>
        <div
          className={`group flex min-h-10 items-center gap-2 rounded-[8px] pr-2 ${
            selected ? 'bg-[#FFF2EC] text-[#C94F32]' : 'text-[#23252D] hover:bg-[#F5F4EF]'
          }`}
          style={{ paddingLeft: `${8 + level * 20}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => setExpandedPaths((paths) => {
                const next = new Set(paths);
                if (next.has(node.path)) next.delete(node.path);
                else next.add(node.path);
                return next;
              })}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-[#868C98] hover:bg-white"
              aria-label={expanded ? `Свернуть ${node.name}` : `Развернуть ${node.name}`}
            >
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${expanded ? '' : '-rotate-90'}`} />
            </button>
          ) : <span className="w-7 shrink-0" />}
          <ArchiveBoxIcon className="h-4 w-4 shrink-0 text-[#F59E0B]" />
          <button
            type="button"
            onClick={() => selectable && setSelectedFolder(node.path)}
            disabled={!selectable}
            className={`min-w-0 flex-1 truncate py-2 text-left text-[13px] ${
              selectable ? 'cursor-pointer' : 'cursor-default font-semibold text-[#868C98]'
            }`}
            title={forbidden ? 'Нельзя перенести в удаляемую папку' : node.path}
          >
            {node.name}
          </button>
          <span className="shrink-0 text-[12px] text-[#868C98]">{node.count}</span>
        </div>
        {hasChildren && expanded && node.children.map((child) => renderNode(child, level + 1))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
      <div role="dialog" aria-modal="true" className="w-full max-w-[640px] overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
        <div className="flex items-center justify-between border-b border-[#EEF0F2] px-6 py-5">
          <div>
            <h2 className="text-[18px] font-bold text-[#23252D]">Удалить папку «{folderName}»</h2>
            <p className="mt-1 text-[13px] text-[#868C98]">
              {step === 'resumes'
                ? 'Выберите резюме, которые нужно перенести перед удалением папки.'
                : 'Выберите папку, куда перенести отмеченные резюме. Остальные будут удалены вместе с папкой.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-[#DDDCD6] text-[#525866] hover:bg-[#F5F4EF]" aria-label="Закрыть">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="flex gap-2 text-[12px] font-semibold uppercase tracking-[0.04em] text-[#868C98]">
            <span className={step === 'resumes' ? 'text-[#E16349]' : ''}>1. Резюме</span>
            <span>→</span>
            <span className={step === 'destination' ? 'text-[#E16349]' : ''}>2. Куда перенести</span>
          </div>

          {step === 'resumes' ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <button type="button" onClick={toggleAll} className="text-[13px] font-semibold text-[#525866] hover:text-[#23252D]">
                  {allSelected ? 'Снять все' : 'Выбрать все'}
                </button>
                <span className="text-[13px] text-[#868C98]">Выбрано: {selectedIds.size} из {candidates.length}</span>
              </div>
              <div className="max-h-[360px] space-y-2 overflow-y-auto rounded-[10px] border border-[#E7E8EC] p-2">
                {candidates.map((item) => {
                  const checked = selectedIds.has(item.candidate_id);
                  return (
                    <label
                      key={item.candidate_id}
                      className={`flex cursor-pointer items-center gap-3 rounded-[8px] px-3 py-2 hover:bg-[#F5F4EF] ${checked ? 'bg-[#F8F7F3]' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleId(item.candidate_id)}
                        className="h-4 w-4 shrink-0 rounded border-[#CDD0D5] accent-[#E16349]"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-semibold text-[#23252D]">{candidateTitle(item)}</span>
                        <span className="block truncate text-[12px] text-[#868C98]">
                          {[item.desired_position, item.location || item.location_city].filter(Boolean).join(' · ') || 'Без деталей'}
                        </span>
                      </span>
                    </label>
                  );
                })}
                {!candidates.length && (
                  <div className="px-3 py-8 text-center text-[13px] text-[#868C98]">
                    В папке нет резюме. Можно просто удалить папку.
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <BaseInput size="sm">
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Найти папку"
                  autoFocus
                  className="w-full bg-transparent text-[14px] text-[#23252D] outline-none placeholder:text-[#A0A4AE]"
                />
              </BaseInput>
              <div className="rounded-[10px] border border-[#E7E8EC] bg-white p-2">
                <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-[#868C98]">Папки</div>
                <div className="max-h-[320px] overflow-y-auto pr-1">
                  {folders.map((node) => renderNode(node))}
                  {!folders.length && <div className="px-2 py-5 text-center text-[13px] text-[#868C98]">Папок пока нет</div>}
                </div>
              </div>
              <div className="rounded-[8px] bg-[#F8F7F3] px-3 py-2 text-[13px] text-[#525866]">
                Куда перенести: <span className="font-semibold text-[#23252D]">{selectedIsAllowed ? selectedNode?.name : 'не выбрано'}</span>
                <span className="mt-1 block text-[12px] text-[#868C98]">
                  Невыбранные резюме ({candidates.length - selectedIds.size}) будут удалены вместе с папкой.
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#EEF0F2] px-6 py-4">
          <button
            type="button"
            disabled={disabled}
            onClick={onDeleteWithResumes}
            className="inline-flex h-11 items-center gap-2 rounded-[9px] border border-[#F3A4A6] px-4 text-[14px] font-semibold text-[#E5484D] hover:bg-[#FFF1F1] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <TrashIcon className="h-4 w-4" />
            Удалить вместе с резюме
          </button>
          <div className="flex flex-wrap gap-3">
            {step === 'destination' ? (
              <button
                type="button"
                onClick={() => setStep('resumes')}
                className="h-11 rounded-[9px] bg-[#F1F0EC] px-5 text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]"
              >
                Назад
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="h-11 rounded-[9px] bg-[#F1F0EC] px-5 text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]"
              >
                Отмена
              </button>
            )}
            {step === 'resumes' ? (
              <button
                type="button"
                disabled={disabled || (!someSelected && candidates.length > 0)}
                onClick={() => {
                  if (!candidates.length) {
                    onDeleteWithResumes();
                    return;
                  }
                  setStep('destination');
                }}
                className="h-11 rounded-[9px] bg-[#E16349] px-5 text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
              >
                {!candidates.length ? 'Удалить папку' : 'Далее'}
              </button>
            ) : (
              <button
                type="button"
                disabled={disabled || !selectedIsAllowed || !someSelected}
                onClick={() => onMoveSelected(Array.from(selectedIds), selectedFolder)}
                className="h-11 rounded-[9px] bg-[#E16349] px-5 text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]"
              >
                {disabled ? 'Удаление...' : 'Перенести и удалить папку'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
